import DealCustomFieldsAPI from '../../api/dealCustomFields';

const state = {
  records: [],
  fieldTypes: {},
  dealValues: {}, // { dealId: { fieldKey: value, ... } }
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingValues: false,
    isSavingValue: false,
  },
};

export const getters = {
  getFields: $state => $state.records,
  getActiveFields: $state => $state.records.filter(f => f.active),
  getFieldTypes: $state => $state.fieldTypes,
  getUIFlags: $state => $state.uiFlags,

  getFieldById: $state => id => {
    return $state.records.find(f => f.id === id);
  },

  getFieldByKey: $state => key => {
    return $state.records.find(f => f.key === key);
  },

  getFieldsForFunnel: $state => funnelId => {
    return $state.records.filter(f => !f.funnel_id || f.funnel_id === funnelId);
  },

  getFieldsForCard: $state => {
    return $state.records.filter(f => f.active && f.show_in_card);
  },

  getFieldsForList: $state => {
    return $state.records.filter(f => f.active && f.show_in_list);
  },

  getFieldsForDetails: $state => {
    return $state.records.filter(f => f.active && f.show_in_details);
  },

  getFieldsByGroup: $state => {
    const grouped = {};
    $state.records
      .filter(f => f.active)
      .forEach(field => {
        const group = field.group_name || 'General';
        if (!grouped[group]) {
          grouped[group] = [];
        }
        grouped[group].push(field);
      });
    return grouped;
  },

  getDealValues: $state => dealId => {
    return $state.dealValues[dealId] || {};
  },

  getDealFieldValue: $state => (dealId, fieldKey) => {
    return $state.dealValues[dealId]?.[fieldKey];
  },

  getRequiredFields: $state => {
    return $state.records.filter(f => f.active && f.required);
  },

  getRequiredFieldsForStage: $state => stageName => {
    return $state.records.filter(
      f =>
        f.active &&
        (f.required ||
          (f.required_in_stages && f.required_in_stages.includes(stageName)))
    );
  },
};

export const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await DealCustomFieldsAPI.get(params);
      const { payload, field_types } = response.data;
      commit('SET_FIELDS', payload);
      if (field_types) {
        commit('SET_FIELD_TYPES', field_types);
      }
      return payload;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchFieldTypes({ commit }) {
    try {
      const response = await DealCustomFieldsAPI.getFieldTypes();
      commit('SET_FIELD_TYPES', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async create({ commit }, fieldData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await DealCustomFieldsAPI.create(fieldData);
      commit('ADD_FIELD', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...fieldData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await DealCustomFieldsAPI.update(id, fieldData);
      commit('UPDATE_FIELD', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await DealCustomFieldsAPI.delete(id);
      commit('REMOVE_FIELD', id);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async reorder({ commit }, { id, position }) {
    try {
      await DealCustomFieldsAPI.reorder(id, position);
      commit('UPDATE_FIELD_POSITION', { id, position });
    } catch (error) {
      throw error;
    }
  },

  async bulkReorder({ commit }, positions) {
    try {
      await DealCustomFieldsAPI.bulkReorder(positions);
      commit('BULK_UPDATE_POSITIONS', positions);
    } catch (error) {
      throw error;
    }
  },

  async fetchDealValues({ commit }, dealId) {
    commit('SET_UI_FLAG', { isFetchingValues: true });
    try {
      const response = await DealCustomFieldsAPI.getValuesForDeal(dealId);
      commit('SET_DEAL_VALUES', { dealId, values: response.data.values });
      return response.data.values;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingValues: false });
    }
  },

  async setDealValues({ commit }, { dealId, values }) {
    commit('SET_UI_FLAG', { isSavingValue: true });
    try {
      const response = await DealCustomFieldsAPI.setValuesForDeal(
        dealId,
        values
      );
      commit('SET_DEAL_VALUES', { dealId, values: response.data.values });
      return response.data.values;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSavingValue: false });
    }
  },

  async setFieldValue({ commit }, { dealId, fieldKey, value }) {
    commit('SET_UI_FLAG', { isSavingValue: true });
    try {
      const response = await DealCustomFieldsAPI.setFieldValue(
        dealId,
        fieldKey,
        value
      );
      commit('SET_DEAL_FIELD_VALUE', {
        dealId,
        fieldKey: response.data.field_key,
        value: response.data.value,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSavingValue: false });
    }
  },
};

export const mutations = {
  SET_FIELDS($state, fields) {
    $state.records = fields;
  },

  SET_FIELD_TYPES($state, fieldTypes) {
    $state.fieldTypes = fieldTypes;
  },

  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },

  ADD_FIELD($state, field) {
    $state.records.push(field);
  },

  UPDATE_FIELD($state, updatedField) {
    const index = $state.records.findIndex(f => f.id === updatedField.id);
    if (index !== -1) {
      $state.records.splice(index, 1, updatedField);
    }
  },

  REMOVE_FIELD($state, id) {
    $state.records = $state.records.filter(f => f.id !== id);
  },

  UPDATE_FIELD_POSITION($state, { id, position }) {
    const field = $state.records.find(f => f.id === id);
    if (field) {
      field.position = position;
    }
    // Re-sort by position
    $state.records.sort((a, b) => a.position - b.position);
  },

  BULK_UPDATE_POSITIONS($state, positions) {
    Object.entries(positions).forEach(([id, position]) => {
      const field = $state.records.find(f => f.id === Number(id));
      if (field) {
        field.position = position;
      }
    });
    // Re-sort by position
    $state.records.sort((a, b) => a.position - b.position);
  },

  SET_DEAL_VALUES($state, { dealId, values }) {
    $state.dealValues = {
      ...$state.dealValues,
      [dealId]: values,
    };
  },

  SET_DEAL_FIELD_VALUE($state, { dealId, fieldKey, value }) {
    if (!$state.dealValues[dealId]) {
      $state.dealValues[dealId] = {};
    }
    $state.dealValues[dealId][fieldKey] = value;
  },

  CLEAR_DEAL_VALUES($state, dealId) {
    if (dealId) {
      delete $state.dealValues[dealId];
    } else {
      $state.dealValues = {};
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
